
import { DateTime, Duration, Interval } from 'luxon';
import React from "react";

import * as d3 from 'd3';

import { zip } from '../utils/utils';
import { Partitioner } from '../utils/Partitioner';
import MultiscaleTimeAxis from '../utils/MultiscaleTimeAxis';
import TornadoCollection from '../utils/TorCollection';
import { IntervalLike, SelectionInterval } from '../utils/SelectionInterval';

interface TimelinePlotProps {
    't-axis': MultiscaleTimeAxis;
    x: number;
    y: number;
    width: number;
    height: number;

    tor_coll: TornadoCollection | null;
    interval: SelectionInterval;
    partitioner: Partitioner;
    cday_offset: Duration;

    onwheel: (intv: Interval) => void;
}

interface TorCountCacheEntry {
    pk: string;
    ntor: number; 
    ntor_base: number;
    subintv: Interval;
}

class TimelinePlot extends React.Component<TimelinePlotProps> {
    tor_counts_cache: {
        data: TorCountCacheEntry[][], 
        counts: number[], 
        intv: Interval
    } | null;
    partitioner: Partitioner;

    root: SVGGElement | null;

    constructor(props: TimelinePlotProps) {
        super(props);

        this.state = {};

        this.tor_counts_cache = null;

        this.partitioner = this.props['partitioner'];
        this.wheel = this.wheel.bind(this);

        this.root = null;
    }

    async updateTorCounts() {
        const t_scale = this.props['t-axis'];

        if (this.props['tor_coll'] !== null) {            
            // Get the tornado data we're going to plot
            let bar_subintvs = t_scale.getBarSubintervals(this.props['cday_offset'], 
                                                          this.props['interval'].isYearly() && this.props['interval'].crossesYear(this.props['cday_offset']));
            let tor_subintvs;

            if (this.props['interval'].isYearly()) {
                tor_subintvs = bar_subintvs.map(sintv => this.props['interval'].intersection(sintv, true));
                [tor_subintvs, bar_subintvs] = [...zip(...[...zip(tor_subintvs, bar_subintvs)].filter(([ts, bs]) => ts !== null))];
            }
            else {
                tor_subintvs = bar_subintvs;
            }

            const tor_counts = await this.props['tor_coll'].getNTornadoes(tor_subintvs, this.partitioner);

            const tor_counts_accum = tor_counts.map(cnt => {
                let p_keys = Object.keys(cnt);
                let p_counts = Object.values(cnt);

                const cumulativeSum = (sum => (value: number) => sum += value)(0);
                var cnt_cumulative = p_counts.map(cumulativeSum).slice(0, p_counts.length - 1);
                cnt_cumulative.unshift(0);

                return [...zip(p_keys, p_counts, cnt_cumulative)].map(el => ({'pk': el[0], 'ntor': el[1], 'ntor_base': el[2]}))
                    .filter(el => el['ntor'] > 0);
            });

            const tor_data = bar_subintvs.map((e, i) => {
                return tor_counts_accum[i].map((el) : TorCountCacheEntry => { (el as TorCountCacheEntry)['subintv'] = e; return el as TorCountCacheEntry; })
            });

            const all_tor_counts = tor_data.filter(el => el.length > 0).map(el => el[el.length - 1]).map(el => el['ntor'] + el['ntor_base']);

            this.tor_counts_cache = {'data': tor_data, 'counts': all_tor_counts, 
                'intv': Interval.fromDateTimes(bar_subintvs[0].start, bar_subintvs[bar_subintvs.length - 1].end)};
        }
    }

    async componentDidMount() {
        await this.updateTorCounts();
        this.drawPlot();

        if (this.root === null || this.root.parentNode === null) {
            return;
        }

        // parentNode ... big ol' yikes
        this.root.parentNode.addEventListener('wheel', this.wheel, {'passive': false});
        // ^ surely there's a way to do this through react? (specifically ^--------------^ this part)
    }

    componentWillUnmount() {
        if (this.root === null || this.root.parentNode === null) {
            return;
        }

        this.root.parentNode.removeEventListener('wheel', this.wheel);
    }

    async componentDidUpdate(prev_props: TimelinePlotProps) {
        this.drawPlot();

        if (this.tor_counts_cache === null || this.props['tor_coll'] !== prev_props['tor_coll'] 
            || this.props['t-axis'] !== prev_props['t-axis']) {
            await this.updateTorCounts();
            this.drawPlot();
        }
    }

    wheel(event: Event) {
        if (this.root === null || this.root.parentElement === null || !(event instanceof WheelEvent)) {
            return;
        }

        // If the event is not actually in the axes bounds, don't bother.
        // Also, another parentNode. Gonna have to figure out a less janky way to do this.
        let rect = this.root.parentElement.getBoundingClientRect();
        let event_x = event.clientX - rect.x;
        let event_y = event.clientY - rect.y;

        if (event_x < this.props['x'] || event_x > this.props['x'] + this.props['width'] ||
            event_y < this.props['y'] || event_y > this.props['y'] + this.props['height']) {
            return;
        }

        // This is why I need the {'passive': false} event handler
        event.preventDefault();
        const t_scale = this.props['t-axis'];

        var new_start: DateTime, new_end: DateTime;

        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            // If the mouse movement is more in the y direction than the x direction, consider it a zoom movement
            const y_sens = 0.005;

            new_start = t_scale.mapInverse(event.clientX - (event.clientX - t_scale.xlb) * (1 + event.deltaY * y_sens));
            new_end = t_scale.mapInverse(event.clientX + (t_scale.xub - event.clientX) * (1 + event.deltaY * y_sens));

            // Enforce maximum zoom
            if (new_end.diff(new_start) < Duration.fromISO('PT2H')) {
                new_start = t_scale.t_bnds_intv.start;
                new_end = t_scale.t_bnds_intv.end;
            }
        }
        else {
            // If the mouse movement is more in the x direction than the y direction, consider it a pan movement
            const x_sens = 0.5;

            new_start = t_scale.mapInverse(t_scale.xlb + x_sens * event.deltaX);
            new_end = t_scale.mapInverse(t_scale.xub + x_sens * event.deltaX);
        }

        let new_intv: Interval | null = Interval.fromDateTimes(new_start, new_end);

        // Enforce minimum zoom
        const zoom_limit_intv = t_scale.getPaddedBounds();
        new_intv = zoom_limit_intv.intersection(new_intv);

        if (this.props['onwheel'] !== undefined && new_intv !== null) {
            this.props['onwheel'](new_intv);
        }
    }
 
    drawPlot() {
        const font_name = 'Trebuchet MS';
        const height = this.props['height'];

        const wrap = (text: d3.Selection<d3.BaseType, unknown, SVGGElement, unknown>) => {
            text.each(function() {
                var text = d3.select(this),
                    words = text.text().split(' ').reverse(),
                    lineNumber = 0;

                const lineHeight = 1.1, // ems
                      y = text.attr("y"),
                      dy = parseFloat(text.attr("dy"));
        
                if (words.length > 1) {
                    text.text(null);
        
                    let iword = 0;
                    let word;
                    while (word = words.pop()) {
                        text.append("tspan")
                            .attr("x", 0)
                            .attr("y", y)
                            .attr('font-size', iword > 0 ? '1.5em' : '1em')
                            .attr("dy", lineNumber++ * lineHeight + dy + "em")
                            .text(word);
                        iword++;
                    }
                }
            });
        }

        const tor_data = this.tor_counts_cache === null ? [] : this.tor_counts_cache['data'];
        const all_tor_counts = this.tor_counts_cache === null ? [] : this.tor_counts_cache['counts'];

        const yub = Math.max(Math.max(...all_tor_counts) * 1.1, 1.1);

        // Create D3 scales
        const t_scale = this.props['t-axis'];

        var y_scale = d3.scaleLinear()
            .domain([0, yub])
            .range([height + this.props['y'], this.props['y']]);

        var root = d3.select(this.root);

        // Clear everything
        root.selectAll('*')
           .remove();
   
        const colors = this.partitioner.colors;
        const part_keys = this.partitioner.part_keys;

        // Plot data
        root.append('g')
            .selectAll('g')
            .data(tor_data)
            .enter()
                .selectAll('rect')
                .data(d => d)
                .enter()
                    .append('rect')
                    .attr('x', d => t_scale.map(d['subintv'].start))
                    .attr('y', d => y_scale(d['ntor'] + d['ntor_base']))
                    .attr('width', d =>  Math.max(0, t_scale.map(d['subintv'].end) - t_scale.map(d['subintv'].start)))
                    .attr('height', d => (height - (y_scale(d['ntor']) - this.props['y'])))
                    .attr('fill', d => colors[part_keys.indexOf(d['pk'])])
                    .attr('clip-path', 'url(#axis-face)');

        // Add t axis
        t_scale.getScales().forEach(scale => {
            root.append('g')
            .attr('transform', 'translate(0, ' + (this.props['y'] + height) + ')')
            .call(d3.axisTop<Date>(scale)
                    .tickSizeOuter(0)
                    .tickSizeInner(height)
                    .ticks(t_scale.getTicks(scale))
                    .tickFormat(t_scale.formatTick.bind(t_scale)))
            .call(g => g.selectAll('.tick line')
                        .attr('stroke-dasharray', '2,2'))
            .call(g => g.selectAll(".tick text")
                        .attr("y", 9)
                        .attr("dx", 0))
            .selectAll(".tick text")
            .call(wrap);
        });


        const mag = Math.floor(Math.log10(yub));
        const mag_ten = Math.pow(10, mag);
        const mag_m1_ten = Math.pow(10, mag - 1);
        let dyt: number;
        if (yub < 1.2 * mag_ten) {
            dyt = mag_m1_ten;
        }
        else if (yub < 3 * mag_ten) {
            dyt = 2 * mag_m1_ten;
        }
        else if (yub < 6 * mag_ten) {
            dyt = 5 * mag_m1_ten;
        }
        else {
            dyt = mag_ten;
        }

        dyt = Math.max(dyt, 1);

        let ticks: number[] = [];
        for (let itick = 0; itick < yub; itick += dyt) {
            ticks.push(itick);
        }

        // Add y axis
        root.append('g')
            .attr('transform', 'translate(' + this.props['x'] + ', 0)')
            .call(d3.axisRight(y_scale)
                    .tickValues(ticks)
                    .tickFormat(x => `${x.valueOf().toFixed(0)}`)
                    .tickSizeOuter(0)
                    .tickSizeInner(this.props['width']))
            .call(g => g.selectAll('.tick line')
                        .attr('stroke-dasharray', '2,2'))
            .call(g => g.selectAll(".tick text")
                        .attr("y", 0)
                        .attr("dx", -this.props['width'] - 4))
                        .attr('text-anchor', 'end');

        const label_offset = (mag + 1) * 5 + 7;
        // Add y axis label
        root.append('text')
            .attr('text-anchor', 'middle')
            .attr('font-size', '15')
            .attr('transform', 'translate(' + (this.props['x'] - label_offset) + ', ' + (this.props['y'] + this.props['height'] / 2) + ') rotate(270)')
            .text('# of Tornadoes')
        
        const orient_label_str = t_scale.getOrientLabel();
        if (orient_label_str !== null) {
            // Draw orientation label (the one in the bottom left corner)
            const tl_labels = root.selectAll('.tick text tspan:nth-child(2)');
            let orient_label_x_cap: number;
            
            if (tl_labels.size() > 0) {
                const first_label = tl_labels.filter((d, i) => i === 0);
                const first_label_node = first_label.node();
                if (first_label_node === null || !(first_label_node instanceof Element)) {
                    orient_label_x_cap = this.props['width'];
                }
                else {
                    const first_label_rect = first_label_node.getBoundingClientRect();
                    orient_label_x_cap = first_label_rect.left - this.props['x'];
    
                    if (first_label_rect.left < this.props['x']){
                        first_label.attr('dx', this.props['x'] - first_label_rect.left);
                    }
                }
            }
            else {
                orient_label_x_cap = this.props['width'];
            }
    
            root.append('g')
                .attr('transform', 'translate(' + this.props['x'] + ', ' + (height + this.props['y']) + ')')
                .attr('fill', 'none')
                .attr('font-size', '10')
                .append('text')
                .attr('class', 'orient-label')
                .attr('fill', 'currentColor')
                .attr('x', 0)
                .attr('y', 9.2)
                .attr('dy', '1.1em')
                .attr('font-size', '1.5em')
                .text(orient_label_str);
            
            const orient_label = root.select('.orient-label').node();
            if (orient_label !== null && orient_label instanceof Element) {
                const orient_label_width = orient_label.getBoundingClientRect().width;
                root.select('.orient-label')
                   .attr('dx', Math.min(0, orient_label_x_cap - orient_label_width - 4))
            }
        }

        // Add bounding rectangle
        root.append('g')
            .append('rect')
            .attr('x', this.props['x'])
            .attr('y', this.props['y'])
            .attr('width', this.props['width'])
            .attr('height', this.props['height'])
            .attr('stroke', '#000000')
            .attr('fill', 'none');

        root.selectAll('text')
            .attr('font-family', font_name);
    }

    render() {
        return (
            <g className='timeline-plot' ref={el => this.root = el}></g>
        );
    }
}

export default TimelinePlot;